/**
 * Shared web / public-share list policies (freeze + ViewingCanvas).
 * Twin of Containd `types/WebSharePolicies.ts` — keep defaults in sync.
 *
 * All four flags apply only on public-share web lists (not inside Containd).
 * `allowLayoutChange: false` or `openOnTap: true` means freeze publishes only `layoutInUse`.
 */

export interface WebSharePolicies {
  /** When false, empty (no children, no canvas SVG bg) activate skips Item Viewer. */
  openEmptyViewer?: boolean;
  allowLayoutChange?: boolean;
  /** When true, first tap activates (no select); extra layouts are not published. */
  openOnTap?: boolean;
  /**
   * Default true. When true, title/Text/button/image links — including in-share
   * `item:` — open in a new tab. When false, even external URLs stay in this window.
   */
  openLinksInNewTab?: boolean;
}

export type ResolvedWebSharePolicies = {
  openEmptyViewer: boolean;
  allowLayoutChange: boolean;
  openOnTap: boolean;
  openLinksInNewTab: boolean;
};

export const DEFAULT_WEB_SHARE_POLICIES: ResolvedWebSharePolicies = {
  openEmptyViewer: true,
  allowLayoutChange: true,
  openOnTap: false,
  openLinksInNewTab: true,
};

/** Resolve from freeze object or serialized JSON string. */
export function resolveWebSharePolicies(
  raw?: WebSharePolicies | string | null,
): ResolvedWebSharePolicies {
  let parsed: WebSharePolicies | null = null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed) {
      try {
        const value = JSON.parse(trimmed) as unknown;
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          parsed = value as WebSharePolicies;
        }
      } catch {
        parsed = null;
      }
    }
  } else if (raw && typeof raw === 'object') {
    parsed = raw;
  }

  return {
    openEmptyViewer: parsed?.openEmptyViewer !== false,
    allowLayoutChange: parsed?.allowLayoutChange !== false,
    openOnTap: parsed?.openOnTap === true,
    openLinksInNewTab: parsed?.openLinksInNewTab !== false,
  };
}

/** Sparse object for freeze (omit when all defaults). */
export function toSparseWebSharePolicies(
  raw?: string | null,
): WebSharePolicies | undefined {
  const resolved = resolveWebSharePolicies(raw);
  const out: WebSharePolicies = {};
  if (!resolved.openEmptyViewer) out.openEmptyViewer = false;
  if (!resolved.allowLayoutChange) out.allowLayoutChange = false;
  if (resolved.openOnTap) out.openOnTap = true;
  if (!resolved.openLinksInNewTab) out.openLinksInNewTab = false;
  return Object.keys(out).length > 0 ? out : undefined;
}
